import { CURRENCY_CONFIG } from '../common';
import { getCurrentLang } from '../lang';

export class CurrencyService {
  /**
   * CurrencyService의 인스턴스를 생성합니다.
   * @param {object} licensePrices - Redux 스토어에서 가져온 라이선스 가격 목록
   */
  constructor(licensePrices) {
    this.licensePrices = licensePrices;
    this.lang = getCurrentLang();
    this.config = CURRENCY_CONFIG[this.lang] || CURRENCY_CONFIG['en'];
  }

  /**
   * 현재 언어 설정에 맞는 통화 기호를 반환합니다.
   * @returns {string} 통화 기호 (예: '₩', '$', '¥', '€')
   */
  getSymbol() {
    return this.config.symbol;
  }

  /**
   * 특정 라이선스에 대한 기본 상품 가격을 반환합니다.
   *   @param {string} license - 국가별 라이센스에 따른 제품 가격을 개별적으로 가져옴 ex) 'Basic', 'Extended', 'Classic', 'Credit_20', 'Credit_50', 'Credit_100'
   * @returns {number} 라이선스에 해당하는 상품 가격
   */
  getProductCost(license) {
    const cost = this.licensePrices.find((lp) => lp.license_type === license);
    if (cost && typeof cost[this.config.apiKey] === 'number') {
      return cost[this.config.apiKey];
    }
    return 0;
  }

  /**
   * 상품과 라이선스에 따른 최종 가격을 계산합니다. (세일 적용)
   * @param {object} product - 상품 정보 객체
   * @param {string} license - 라이선스 종류 (예: 'Basic', 'Extended')
   * @returns {number} 계산된 최종 가격
   */
  getPrice(product, license) {
    const saleMultiplier = product.is_sale ? 0.8 : 1;
    let basePrice = 0;

    // 1. VIP 가격 우선 확인
    if (product.vip_krw_price !== null && product[this.config.vipKey]) {
      try {
        const vipPriceData = JSON.parse(product[this.config.vipKey]);
        const priceInfo = vipPriceData.find((p) => p.license === license);
        if (priceInfo && typeof priceInfo.price === 'number') {
          basePrice = priceInfo.price;
          return basePrice * saleMultiplier;
        }
      } catch (e) {
        console.error('Failed to parse VIP price data:', e);
      }
    }

    // 2. 일반 가격 확인
    const licensePriceInfo = this.licensePrices.find((lp) => lp.license_type === license);
    if (licensePriceInfo && typeof licensePriceInfo[this.config.apiKey] === 'number') {
      basePrice = licensePriceInfo[this.config.apiKey];
    }

    return basePrice * saleMultiplier;
  }

  /**
   * 계산된 가격을 통화 기호와 함께 포맷팅된 문자열로 반환합니다.
   * @param {number} price - 포맷팅할 가격
   * @returns {string} 포맷팅된 가격 문자열 (예: '₩10,000')
   */
  _format(price) {
    if (typeof price !== 'number') {
      return `${this.getSymbol()}0`;
    }
    return `${this.getSymbol()}${price.toLocaleString()}`;
  }

  /**
   * 상품과 라이선스를 받아 포맷팅된 최종 가격 문자열을 반환합니다.
   * @param {object} product - 상품 정보 객체
   * @param {string} license - 라이선스 종류
   * @returns {string} 포맷팅된 최종 가격 문자열
   */
  getFormattedPrice(product, license) {
    const calculatedPrice = this.getPrice(product, license);
    return this._format(calculatedPrice);
  }

  /**
   * 총액, 쿠폰 정보를 바탕으로 최종 결제 금액 관련 정보를 계산합니다.
   * @param {[object]} list - 계산해야하는 상품 리스트
   * @param {object|null} couponInfo - 적용할 쿠폰 정보
   * @returns {{totalAmount: number, vat: number, couponDiscount: number}} - 최종 금액, 부가세, 할인 금액
   */
  calculateTotals(list, couponInfo) {
    // 1. 상품 원가 총합(VAT 제외)을 먼저 계산합니다.
    const normalPrice = list.reduce((total, item) => {
      const price = this.getPrice(item.product, item.cart_product_license || 'Basic');
      return total + price;
    }, 0);

    let vat = 0;
    // 2. 부가세는 한국(kr)일 경우에만 상품 원가 기준으로 계산합니다.
    if (this.lang === 'kr') {
      vat = Math.floor(normalPrice * 0.1);
    }

    // 3. 할인의 기준이 되는 소계(상품가 + 부가세)를 계산합니다.
    const subTotal = normalPrice + vat;
    let couponDiscount = 0;

    // 4. 쿠폰 할인을 소계 기준으로 계산합니다.
    if (couponInfo && couponInfo.discount_value > 0) {
      couponDiscount = Math.round(subTotal * (couponInfo.discount_value / 100));
    }

    // 5. 최종 결제 금액을 계산합니다.
    const totalAmount = subTotal - couponDiscount;

    return {
      totalAmount: Math.round(totalAmount),
      vat,
      couponDiscount,
      normalPrice, // BUG FIX: 정상적으로 계산된 normalPrice를 반환
    };
  }
}

export default CurrencyService;
